import { z } from "zod";

import { TEAM_MEMBER_VALUES } from "~/common/constants";
import {
  PHONE_SCHEDULE_VARIANTS,
  PROVIDER_SCHEDULE_METHODS,
} from "../constants/teamMembers";

export const providerScheduleMethodsSchema = z.nativeEnum(
  PROVIDER_SCHEDULE_METHODS,
);

export const phoneScheduleVariantsSchema = z.nativeEnum(
  PHONE_SCHEDULE_VARIANTS,
);

export const emailScheduleMethodSchema = z.object({
  type: z.literal(PROVIDER_SCHEDULE_METHODS.EMAIL),
  active: z.boolean(),
  data: z.object({
    email: z.string().email(),
  }),
});

export const phoneScheduleMethodSchema = z.object({
  type: z.literal(PROVIDER_SCHEDULE_METHODS.PHONE),
  active: z.boolean(),
  data: z.object({
    number: z.string(),
    ext: z.string().optional(),
    variant: phoneScheduleVariantsSchema,
  }),
});

export const calendarScheduleMethodSchema = z.object({
  type: z.literal(PROVIDER_SCHEDULE_METHODS.CALENDAR),
  active: z.boolean(),
  data: z.object({
    link: z.string().url(),
  }),
});

export const providerScheduleContactMethodSchema = z.discriminatedUnion(
  "type",
  [
    emailScheduleMethodSchema,
    phoneScheduleMethodSchema,
    calendarScheduleMethodSchema,
  ],
);

export const teamMemberDetailsBaseSchema = z.object({
  signature_name: z
    .string()
    .min(1, "Please enter signature name.")
    .max(200, "Signature name cannot exceed 200 characters."),
  preferred_reference: z
    .string()
    .min(1, "Please enter preferred reference.")
    .max(200, "Preferred reference cannot exceed 200 characters."),
  name: z
    .string()
    .min(1, "Please enter first name.")
    .max(30, "First name cannot exceed 30 characters."),
  last_name: z
    .string()
    .min(1, "Please enter last name.")
    .max(30, "Last name name cannot exceed 30 characters."),
});

export const staffDetailsSchema = teamMemberDetailsBaseSchema.extend({
  type: z.literal(TEAM_MEMBER_VALUES.staff),
});

export const providerDetailsSchema = teamMemberDetailsBaseSchema
  .extend({
    type: z.literal(TEAM_MEMBER_VALUES.provider),
    enable_phone_number: z.boolean(),
    enable_email: z.boolean(),
    enable_booking_link: z.boolean(),
    phone_number: z.string(),
    phone_number_mode: phoneScheduleVariantsSchema.optional(),
    phone_number_extension: z.string().optional(),
    email: z.string(),
    booking_link: z.string(),
  })
  .superRefine((data, ctx) => {
    const {
      enable_phone_number,
      enable_email,
      enable_booking_link,
      phone_number,
      phone_number_mode,
      email,
      booking_link,
    } = data;

    if (!enable_phone_number && !enable_email && !enable_booking_link) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "You must specify at least one scheduling method.",
        path: ["enable_phone_number"],
      });
    }

    if (enable_phone_number) {
      const phoneRegex = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
      if (!phoneRegex.test(phone_number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be a valid phone number",
          path: ["phone_number"],
        });
      }

      if (!phone_number_mode?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Phone number mode is required when phone number is enabled.",
          path: ["phone_number_mode"],
        });
      }
    }

    if (enable_email) {
      if (!z.string().email().safeParse(email).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be a valid email address",
          path: ["email"],
        });
      }
    }

    if (enable_booking_link) {
      if (!z.string().url().safeParse(booking_link).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be a valid url",
          path: ["booking_link"],
        });
      }
    }
  });

export const teamMemberDetailsSchema = z.union([
  staffDetailsSchema,
  providerDetailsSchema,
]);
